import { useEffect, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { LetterPackPurchaseModal } from '@/components/letters/LetterPackPurchaseModal'
import { useAuth } from '@/hooks/use-auth'
import { splitAddressDisplay } from '@/lib/address'
import { deleteBiddleDraft, saveBiddleDraft } from '@/lib/biddle-drafts'
import { callableErrorMessage } from '@/lib/callable-errors'
import { createBiddle } from '@/lib/callables'
import { openSignIn } from '@/lib/signin-navigation'
import { openVerifyIdentity } from '@/lib/verify-identity-navigation'
import { cn } from '@/lib/utils'
import type { BiddleDraft, BiddleDraftStep, Property } from '@/types/models'

const NOTES_MAX = 240
const FLEXIBLE = "I'm flexible"

type Step = 'agreement' | 'price' | 'terms' | 'timing' | 'extras' | 'review'

const STEPS: Step[] = ['agreement', 'price', 'terms', 'timing', 'extras', 'review']
const DRAFT_STEPS: BiddleDraftStep[] = ['price', 'terms', 'timing', 'extras', 'review']

function formatExpiryDisplay(ymd: string): string {
  const d = new Date(`${ymd}T12:00:00`)
  if (Number.isNaN(d.getTime())) return ymd
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function normalizeFlexibleField(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed
  if (trimmed.toLowerCase().includes('flex')) return FLEXIBLE
  return trimmed
}

/** Digits-only price display with thousand separators (e.g. 350000 → 350,000). */
function formatPriceInput(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''
  return Number(digits).toLocaleString('en-AU')
}

function expiryToEpochSeconds(dateStr: string): number {
  const d = new Date(`${dateStr}T23:59:59`)
  return Math.floor(d.getTime() / 1000)
}

/** Local calendar date `days` ahead, as `YYYY-MM-DD` for `<input type="date">`. */
function datePlusDaysYmd(days: number): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + days)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function stepIndex(step: Step): number {
  return STEPS.indexOf(step)
}

/**
 * Create Biddle flow — short steps with back/forth, address always visible.
 */
export function CreateBiddleWizard({
  property,
  onCancel,
  onCreated,
  initialDraft = null,
}: {
  property: Property
  onCancel: () => void
  onCreated: (biddleId: string) => void
  initialDraft?: BiddleDraft | null
}) {
  const { user, profile, isGuest } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const verified = Boolean(profile?.isIdentityVerified)

  const [step, setStep] = useState<Step>('agreement')
  const [agreed, setAgreed] = useState(false)
  const [price, setPrice] = useState('')
  const [settlement, setSettlement] = useState('60')
  const [deposit, setDeposit] = useState('10')
  const [flexibleSettlement, setFlexibleSettlement] = useState(false)
  const [flexibleDeposit, setFlexibleDeposit] = useState(false)
  const [notes, setNotes] = useState('')
  const [isOpenOffer, setIsOpenOffer] = useState(false)
  const [expiry, setExpiry] = useState('')
  const [sendLetter, setSendLetter] = useState(false)
  const [buyPackOpen, setBuyPackOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [draftNotice, setDraftNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hydratedDraftId, setHydratedDraftId] = useState<string | null>(null)

  const letterCredits = profile?.letterCredits ?? 0
  const { street, locality } = splitAddressDisplay(property.fullAddress)
  const minExpiryDate = datePlusDaysYmd(7)
  const canSaveDraft = step !== 'agreement' && agreed

  useEffect(() => {
    if (!initialDraft || initialDraft.propertyId !== property.id) return
    if (hydratedDraftId === initialDraft.propertyId) return
    setAgreed(initialDraft.agreed)
    setPrice(initialDraft.price)
    setSettlement(initialDraft.settlement)
    setDeposit(initialDraft.deposit)
    setFlexibleSettlement(initialDraft.flexibleSettlement)
    setFlexibleDeposit(initialDraft.flexibleDeposit)
    setIsOpenOffer(initialDraft.isOpenOffer)
    setExpiry(initialDraft.expiry)
    setNotes(initialDraft.notes)
    setSendLetter(initialDraft.sendLetter)
    // Deep-link prefill may set fields without agreement — stay on agreement first.
    setStep(initialDraft.agreed ? initialDraft.step : 'agreement')
    setHydratedDraftId(initialDraft.propertyId)
  }, [initialDraft, property.id, hydratedDraftId])

  const parsedPrice = Number(String(price).replace(/[^\d.]/g, ''))
  const settlementValue = flexibleSettlement ? FLEXIBLE : normalizeFlexibleField(settlement)
  const depositValue = flexibleDeposit ? FLEXIBLE : normalizeFlexibleField(deposit)
  // Minimum expiry: one week from today (YYYY-MM-DD compares lexicographically).
  const expiryValid = isOpenOffer || (Boolean(expiry) && expiry >= minExpiryDate)

  const priceValid = Number.isFinite(parsedPrice) && parsedPrice > 0
  const termsValid = Boolean(settlementValue) && Boolean(depositValue)
  const timingValid = expiryValid
  const notesValid = notes.length < NOTES_MAX
  const isFormValid = priceValid && termsValid && timingValid && notesValid

  function goBack() {
    setError(null)
    const i = stepIndex(step)
    if (i <= 0) {
      onCancel()
      return
    }
    setStep(STEPS[i - 1]!)
  }

  function goNext() {
    setError(null)
    const i = stepIndex(step)
    if (i < STEPS.length - 1) setStep(STEPS[i + 1]!)
  }

  async function onSaveDraft() {
    if (!canSaveDraft || !DRAFT_STEPS.includes(step as BiddleDraftStep)) return

    if (!user || isGuest) {
      openSignIn(navigate, location, `${location.pathname}${location.search}`)
      return
    }

    setSavingDraft(true)
    setError(null)
    setDraftNotice(null)
    try {
      await saveBiddleDraft(user.uid, {
        propertyId: property.id,
        fullAddress: property.fullAddress,
        step: step as BiddleDraftStep,
        agreed: true,
        price,
        settlement,
        deposit,
        flexibleSettlement,
        flexibleDeposit,
        isOpenOffer,
        expiry,
        notes,
        sendLetter,
      })
      setDraftNotice('Draft saved')
    } catch (err) {
      setError(callableErrorMessage(err))
    } finally {
      setSavingDraft(false)
    }
  }

  async function onSubmit() {
    if (!user || !isFormValid) return

    if (!verified) {
      openVerifyIdentity(
        navigate,
        location,
        `${location.pathname}${location.search}`,
        'create-biddle',
      )
      return
    }

    setBusy(true)
    setError(null)
    setDraftNotice(null)
    try {
      const result = await createBiddle({
        buyerID: user.uid,
        propertyID: property.id,
        fullAddress: property.fullAddress,
        price: Math.round(parsedPrice),
        settlement: settlementValue,
        deposit: depositValue,
        notes: notes.trim(),
        expiry: isOpenOffer ? null : expiryToEpochSeconds(expiry),
        isOpenOffer,
        sendLetter: sendLetter && letterCredits > 0,
      })
      try {
        await deleteBiddleDraft(user.uid, property.id)
      } catch {
        // Non-fatal — Biddle was created
      }
      onCreated(result.biddleId)
    } catch (err) {
      setError(callableErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const settlementDisplay = flexibleSettlement
    ? FLEXIBLE
    : settlementValue.toLowerCase().includes('day')
      ? settlementValue
      : `${settlementValue} days`
  const depositDisplay = flexibleDeposit
    ? FLEXIBLE
    : depositValue.includes('%')
      ? depositValue
      : `${depositValue}%`

  const canContinue =
    (step === 'agreement' && agreed) ||
    (step === 'price' && priceValid) ||
    (step === 'terms' && termsValid) ||
    (step === 'timing' && timingValid) ||
    (step === 'extras' && notesValid) ||
    (step === 'review' && isFormValid)

  return (
    <div className="flex flex-col gap-5">
      <AddressHeader street={street} locality={locality} />

      <StepDots current={stepIndex(step)} total={STEPS.length} />

      {step === 'agreement' ? (
        <StepBody
          title="Quick heads-up"
          subtitle="A Biddle is a non-binding expression of interest — not a contract."
        >
          <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted">
            <li>The owner can view, ignore, or choose to chat — entirely up to them.</li>
            <li>If they chat, you stay anonymous until both of you agree to share names.</li>
            {!verified ? (
              <li>You’ll need to be identity-verified to send a Biddle.</li>
            ) : null}
          </ul>
          <FriendlyCheck checked={agreed} onChange={setAgreed} className="mt-1">
            I understand — let’s continue
          </FriendlyCheck>
        </StepBody>
      ) : null}

      {step === 'price' ? (
        <StepBody title="What’s your price?" subtitle="Suggest what you’d pay — in Australian dollars.">
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted">
              $
            </span>
            <Input
              id="modal-price"
              inputMode="numeric"
              placeholder="850,000"
              value={price}
              onChange={(e) => setPrice(formatPriceInput(e.target.value))}
              className="h-14 rounded-2xl border-border/80 bg-surface pl-9 text-lg font-semibold shadow-none focus-visible:ring-1"
              autoFocus
            />
          </div>
        </StepBody>
      ) : null}

      {step === 'terms' ? (
        <StepBody title="Settlement & deposit" subtitle="You can stay flexible on either.">
          <div className="grid gap-4 sm:grid-cols-2">
            <FieldCard label="Settlement">
              {!flexibleSettlement ? (
                <div className="relative">
                  <Input
                    id="modal-settlement"
                    placeholder="60"
                    value={settlement}
                    onChange={(e) => setSettlement(e.target.value)}
                    className="h-12 rounded-2xl border-border/80 bg-white pr-12 shadow-none focus-visible:ring-1"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted">
                    days
                  </span>
                </div>
              ) : (
                <p className="rounded-2xl bg-biddle-blue/10 px-4 py-3 text-sm font-medium text-biddle-blue">
                  {FLEXIBLE}
                </p>
              )}
              <FriendlyCheck
                checked={flexibleSettlement}
                onChange={(on) => {
                  setFlexibleSettlement(on)
                  if (on) setSettlement(FLEXIBLE)
                  else setSettlement('60')
                }}
              >
                {FLEXIBLE}
              </FriendlyCheck>
            </FieldCard>

            <FieldCard label="Deposit">
              {!flexibleDeposit ? (
                <div className="relative">
                  <Input
                    id="modal-deposit"
                    placeholder="10"
                    value={deposit}
                    onChange={(e) => setDeposit(e.target.value)}
                    className="h-12 rounded-2xl border-border/80 bg-white pr-8 shadow-none focus-visible:ring-1"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted">
                    %
                  </span>
                </div>
              ) : (
                <p className="rounded-2xl bg-biddle-blue/10 px-4 py-3 text-sm font-medium text-biddle-blue">
                  {FLEXIBLE}
                </p>
              )}
              <FriendlyCheck
                checked={flexibleDeposit}
                onChange={(on) => {
                  setFlexibleDeposit(on)
                  if (on) setDeposit(FLEXIBLE)
                  else setDeposit('10')
                }}
              >
                {FLEXIBLE}
              </FriendlyCheck>
            </FieldCard>
          </div>
        </StepBody>
      ) : null}

      {step === 'timing' ? (
        <StepBody
          title="How long should it stay open?"
          subtitle="Leave it open, or set an expiry date."
        >
          <FriendlyCheck
            checked={isOpenOffer}
            onChange={(on) => {
              setIsOpenOffer(on)
              if (on) setExpiry('')
            }}
          >
            <span className="font-medium text-navy">Open offer</span>
            <span className="mt-0.5 block text-sm font-normal text-muted">
              No fixed expiry — stays active until you withdraw it.
            </span>
          </FriendlyCheck>

          <div className="flex items-center gap-3" aria-hidden>
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold tracking-wide text-muted">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className={cn('space-y-2', isOpenOffer && 'opacity-50')}>
            <Label htmlFor="modal-expiry" className="text-muted">
              Expiry date
            </Label>
            <Input
              id="modal-expiry"
              type="date"
              min={minExpiryDate}
              value={expiry}
              disabled={isOpenOffer}
              onChange={(e) => {
                setIsOpenOffer(false)
                setExpiry(e.target.value)
              }}
              className="h-12 rounded-2xl border-border/80 bg-surface shadow-none focus-visible:ring-1"
            />
            <p className="text-xs text-muted">Must be at least one week from today.</p>
          </div>
        </StepBody>
      ) : null}

      {step === 'extras' ? (
        <StepBody
          title="Anything else?"
          subtitle="Optional — add a note or send a letter to the owner."
        >
          <div className="space-y-2">
            <Label htmlFor="modal-notes" className="text-muted">
              Note to the owner
            </Label>
            <div className="relative">
              <Textarea
                id="modal-notes"
                maxLength={NOTES_MAX - 1}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional message…"
                className="min-h-[110px] rounded-2xl border-border/80 bg-surface pb-7 shadow-none focus-visible:ring-1"
              />
              <span className="pointer-events-none absolute bottom-2.5 right-3 text-xs text-muted">
                {notes.length}/{NOTES_MAX}
              </span>
            </div>
          </div>

          {letterCredits > 0 ? (
            <FriendlyCheck checked={sendLetter} onChange={setSendLetter}>
              <span className="font-medium text-navy">Send a physical letter</span>
              <span className="mt-0.5 block text-sm font-normal text-muted">
                Uses 1 of your {letterCredits} letter credit{letterCredits === 1 ? '' : 's'}.
              </span>
            </FriendlyCheck>
          ) : (
            <p className="rounded-2xl bg-surface px-4 py-3 text-sm text-muted">
              No letter credits.{' '}
              <button
                type="button"
                className="font-semibold text-biddle-blue underline"
                onClick={() => setBuyPackOpen(true)}
              >
                Buy a pack
              </button>
            </p>
          )}
        </StepBody>
      ) : null}

      {step === 'review' ? (
        <StepBody title="Review your Biddle" subtitle="Check everything looks right before you send it.">
          <div className="overflow-hidden rounded-2xl border border-border/70 bg-surface/60">
            <SummaryRow label="Price" value={`$${formatPriceInput(String(Math.round(parsedPrice)))}`} />
            <SummaryRow label="Settlement" value={settlementDisplay} />
            <SummaryRow label="Deposit" value={depositDisplay} />
            <SummaryRow
              label="Expiry"
              value={isOpenOffer ? 'Open offer (no expiry)' : formatExpiryDisplay(expiry)}
            />
            <SummaryRow
              label="Note"
              value={notes.trim() ? notes.trim() : 'None'}
            />
            <SummaryRow
              label="Letter"
              value={
                sendLetter && letterCredits > 0
                  ? 'Yes — send a physical letter'
                  : 'No letter'
              }
              last
            />
          </div>
        </StepBody>
      ) : null}

      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {draftNotice ? <p className="text-sm text-success">{draftNotice}</p> : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/80 pt-4">
        <Button type="button" variant="ghost" onClick={goBack} disabled={busy || savingDraft}>
          Back
        </Button>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canSaveDraft ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy || savingDraft}
              onClick={() => void onSaveDraft()}
            >
              {savingDraft ? <Spinner /> : null}
              Save draft
            </Button>
          ) : null}
          {step === 'review' ? (
            <Button
              type="button"
              variant="primary"
              disabled={busy || savingDraft || !isFormValid}
              onClick={() => void onSubmit()}
            >
              {busy ? <Spinner className="border-white border-t-transparent" /> : null}
              Confirm & Submit
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              disabled={!canContinue || savingDraft}
              onClick={goNext}
            >
              Continue
            </Button>
          )}
        </div>
      </div>

      <LetterPackPurchaseModal
        open={buyPackOpen}
        onClose={() => setBuyPackOpen(false)}
        onPurchased={() => {
          setSendLetter(true)
          setBuyPackOpen(false)
        }}
      />
    </div>
  )
}

function AddressHeader({ street, locality }: { street: string; locality: string }) {
  return (
    <div className="pr-8">
      <p className="text-sm font-medium text-biddle-blue">New Biddle</p>
      <h2 className="mt-1 text-2xl font-bold tracking-tight text-navy">
        {street || 'Address unavailable'}
      </h2>
      {locality ? <p className="mt-1 text-sm text-muted">{locality}</p> : null}
    </div>
  )
}

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 rounded-full transition-all duration-300',
            i === current ? 'w-6 bg-biddle-blue' : i < current ? 'w-1.5 bg-biddle-blue/50' : 'w-1.5 bg-border',
          )}
        />
      ))}
    </div>
  )
}

function StepBody({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold text-navy">{title}</h3>
        <p className="mt-1 text-sm text-muted">{subtitle}</p>
      </div>
      {children}
    </div>
  )
}

function FieldCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-surface/80 p-4">
      <Label className="text-navy">{label}</Label>
      {children}
    </div>
  )
}

function SummaryRow({
  label,
  value,
  last,
}: {
  label: string
  value: string
  last?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 px-4 py-3 text-sm',
        !last && 'border-b border-border/60',
      )}
    >
      <span className="shrink-0 text-muted">{label}</span>
      <span className="min-w-0 text-right font-medium text-navy whitespace-pre-wrap break-words">
        {value}
      </span>
    </div>
  )
}

function FriendlyCheck({
  checked,
  onChange,
  children,
  className,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'flex w-full items-start gap-3 rounded-2xl border px-4 py-3.5 text-left text-sm transition',
        checked
          ? 'border-biddle-blue/40 bg-biddle-blue/5 text-charcoal'
          : 'border-border/80 bg-white text-charcoal hover:border-biddle-blue/30 hover:bg-surface',
        className,
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition',
          checked ? 'border-biddle-blue bg-biddle-blue text-white' : 'border-border bg-white',
        )}
      >
        {checked ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  )
}
